import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/it";
import "moment/locale/fr";
import "moment/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Toaster } from "sonner";
import "./i18n";
import React, { Suspense, lazy } from "react";

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Auth context
import { AuthProvider, useAuth } from "./contexts";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import ErrorBoundary from "./components/ErrorBoundary";
import ForcePasswordChange from "./components/ForcePasswordChange";

// Core pages (loaded immediately)
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import CalendarPage from "./pages/CalendarPage";

// Lazy-loaded pages (loaded on demand)
const ChatPage = lazy(() => import("./pages/ChatPage"));
const TeamsPage = lazy(() => import("./pages/admin/TeamsPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const ActivityPage = lazy(() => import("./pages/ActivityPage"));
const BackupPage = lazy(() => import("./pages/BackupPage"));
const MonitorPage = lazy(() => import("./pages/MonitorPage"));
const QCDashboard = lazy(() => import("./pages/QCDashboard"));
const ITAdminPage = lazy(() => import("./pages/ITAdminPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
  </div>
);

// Expose localizer for calendar pages
export const localizer = momentLocalizer(moment);

// TelcaVoIP Badge Component
const TelcaVoIPBadge = () => (
  <a
    href="https://telcavoip.ai"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition-shadow text-sm text-gray-700 hover:text-gray-900"
  >
    <span className="text-xs text-gray-500">Powered by</span>
    <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">TelcaVoIP AI</span>
  </a>
);

// Main App
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
        <PWAInstallPrompt />
        <TelcaVoIPBadge />
      </AuthProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const auth = useAuth();
  const user = auth?.user;
  const [mustChange, setMustChange] = React.useState(false);

  React.useEffect(() => {
    if (user?.must_change_password) setMustChange(true);
  }, [user]);

  return (
    <>
      {mustChange && user && (
        <ForcePasswordChange user={user} onChanged={() => setMustChange(false)} />
      )}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams"
            element={
              <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
                <TeamsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={["superadmin", "admin", "operator"]}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity"
            element={
              <ProtectedRoute allowedRoles={["superadmin", "admin", "operator"]}>
                <ActivityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/backup"
            element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <BackupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/monitor"
            element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <MonitorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute allowedRoles={["superadmin", "admin", "operator", "installer"]}>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qc-dashboard"
            element={
              <ProtectedRoute allowedRoles={["quality_control"]}>
                <QCDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/it-admin"
            element={
              <ProtectedRoute allowedRoles={["superadmin", "it_admin"]}>
                <ITAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/news"
            element={
              <ProtectedRoute>
                <NewsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
